import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { StoryResponse, PersonProfile, TimeRange } from '../../api/types';

/**
 * Two stories, field by field.
 *
 * Built for "query vs match" and reused by PD-5 for the OTHER comparison §11
 * names: the same scenario side submitted on the legacy path and on the split
 * path (spec §5.5 — "replay intake scenarios on both paths; compare submitted
 * story_text coverage"). That reuse needs nothing but the labels: the fields
 * being compared are the same fields, and the mirror note ("A.them ↔ B.you")
 * is the one thing that is meaningless between two runs of the SAME person,
 * so it can be turned off.
 *
 * Every added prop is optional and defaults to the match-comparison behaviour,
 * so `MatchDetailPage` is untouched.
 */
interface Props {
  storyA: StoryResponse;
  storyB: StoryResponse;
  /** Card headings. Default: "Story A (Query)" / "Story B (Match)". */
  labelA?: string;
  labelB?: string;
  /** A line under the heading — a run's path, timestamp, pebble id. */
  subtitleA?: string;
  subtitleB?: string;
  title?: string;
  /** The A.them ↔ B.you note. False when the two stories are one person's. */
  showMirror?: boolean;
}

// Helper to render chips
function ChipList({ items, color = 'gray' }: { items: string[]; color?: string }) {
  if (!items || items.length === 0) return <span className="text-gray-400 text-xs">None</span>;

  const colorClasses: Record<string, string> = {
    gray: 'bg-gray-100 text-gray-700',
    blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
    amber: 'bg-amber-100 text-amber-700',
    green: 'bg-green-100 text-green-700',
  };

  return (
    <div className="flex flex-wrap gap-1">
      {items.slice(0, 5).map((item, i) => (
        <span
          key={i}
          className={`text-xs px-1.5 py-0.5 rounded ${colorClasses[color] || colorClasses.gray}`}
        >
          {item}
        </span>
      ))}
      {items.length > 5 && (
        <span className="text-xs text-gray-400">+{items.length - 5} more</span>
      )}
    </div>
  );
}

// Format a parsed time interval for display. Coarse precisions (day/week/
// month/season) render date-only; minute/hour render date + time. The end is
// exclusive, so for coarse precisions we step back 1ms to read inclusively
// ("May 24", not "May 24 – May 25").
function formatTimeRange(range: TimeRange): string {
  const coarse = range.precision !== 'minute' && range.precision !== 'hour';
  const opts: Intl.DateTimeFormatOptions = coarse
    ? { year: 'numeric', month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
  const startStr = new Date(range.startMs).toLocaleString(undefined, opts);
  const endStr = new Date(coarse ? range.endMs - 1 : range.endMs).toLocaleString(undefined, opts);
  return startStr === endStr ? startStr : `${startStr} – ${endStr}`;
}

// Collapsible profile section
function ProfileSummary({ profile, label }: { profile: PersonProfile | undefined; label: string }) {
  const [expanded, setExpanded] = useState(false);

  if (!profile) return null;

  // Key identifiers
  const identity = [profile.name, profile.nickname, profile.gender, profile.age,
    profile.instagram ? `@${profile.instagram}` : null]
    .filter(Boolean)
    .join(' · ');

  // Physical summary
  const physical = [profile.height, profile.eyeColor, profile.hairColor, profile.hairStyle]
    .filter(Boolean)
    .join(', ');

  // Life summary
  const life = [profile.occupation, profile.education].filter(Boolean).join(' · ');

  const hasData = identity || physical || life ||
    profile.descriptors?.length > 0 ||
    profile.hobbiesInterests?.length > 0;

  if (!hasData) {
    return (
      <div>
        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</div>
        <span className="text-gray-400 text-xs">No data</span>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs text-gray-500 uppercase tracking-wide mb-1 hover:text-gray-700"
      >
        {label}
        <span className="text-gray-400 text-xs normal-case">
          {expanded ? '−' : '+'}
        </span>
      </button>

      {!expanded ? (
        // Compact view
        <div className="space-y-1">
          {identity && <div className="text-sm text-gray-700">{identity}</div>}
          {profile.descriptors?.length > 0 && (
            <ChipList items={profile.descriptors} />
          )}
        </div>
      ) : (
        // Expanded view
        <div className="space-y-2 text-sm">
          {identity && (
            <div>
              <span className="text-gray-500">Identity:</span> {identity}
            </div>
          )}
          {physical && (
            <div>
              <span className="text-gray-500">Physical:</span> {physical}
            </div>
          )}
          {life && (
            <div>
              <span className="text-gray-500">Life:</span> {life}
            </div>
          )}
          {profile.clothesAtEvent?.length > 0 && (
            <div>
              <span className="text-gray-500">Wore:</span>{' '}
              <ChipList items={profile.clothesAtEvent} />
            </div>
          )}
          {profile.descriptors?.length > 0 && (
            <div>
              <span className="text-gray-500">Descriptors:</span>{' '}
              <ChipList items={profile.descriptors} />
            </div>
          )}
          {profile.hobbiesInterests?.length > 0 && (
            <div>
              <span className="text-gray-500">Interests:</span>{' '}
              <ChipList items={profile.hobbiesInterests} color="green" />
            </div>
          )}
          {profile.habits?.length > 0 && (
            <div>
              <span className="text-gray-500">Habits:</span>{' '}
              <ChipList items={profile.habits} color="amber" />
            </div>
          )}
          {profile.socialHandles?.length > 0 && (
            <div>
              <span className="text-gray-500">Social:</span>{' '}
              <ChipList items={profile.socialHandles} color="blue" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StoryCard({
  story,
  label,
  subtitle,
  bgClass,
}: {
  story: StoryResponse;
  label: string;
  subtitle?: string;
  bgClass: string;
}) {
  return (
    <div className={`rounded-lg p-4 ${bgClass}`} data-testid="side-by-side-card" data-label={label}>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="font-semibold text-gray-700">{label}</span>
        {subtitle && <span className="text-xs text-gray-500">{subtitle}</span>}
        <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
          story.type === 'stranger' ? 'bg-gray-200 text-gray-700' :
          story.type === 'crush' ? 'bg-pink-200 text-pink-700' :
          'bg-purple-200 text-purple-700'
        }`}>
          {story.type}
        </span>
        <Link
          to={`/stories/${story.id}`}
          target="_blank"
          className="text-xs text-pebbl-600 font-mono hover:underline break-all"
        >
          {story.id}
        </Link>
      </div>

      <p className="text-sm text-gray-700 mb-4">{story.rawText}</p>

      <div className="space-y-3 text-sm">
        {/* Location */}
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Location</div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{story.location.city}</span>
            {story.location.venue && (
              <span className="text-gray-600">@ {story.location.venue}</span>
            )}
          </div>
          {story.location.microCues?.length > 0 && (
            <div className="mt-1">
              <ChipList items={story.location.microCues} color="blue" />
            </div>
          )}
          {story.sharedPlaceOfInteraction && (
            <div className="mt-1 text-xs text-gray-600">
              <span className="text-gray-500">Shared place:</span> {story.sharedPlaceOfInteraction}
            </div>
          )}
          {story.sharedCity && story.sharedCity !== story.location.city && (
            <div className="mt-1 text-xs text-gray-600">
              <span className="text-gray-500">Shared city:</span> {story.sharedCity}
            </div>
          )}
        </div>

        {/* Time */}
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Time</div>
          <div className="text-gray-700">
            {story.time.date || story.time.dayPart || 'Not specified'}
            {story.time.time && ` at ${story.time.time}`}
          </div>
          {story.time.range && (
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                {formatTimeRange(story.time.range)}
              </span>
              <span className="text-[10px] text-gray-400 uppercase tracking-wide">
                {story.time.range.precision}
              </span>
            </div>
          )}
          {story.frequency?.pattern && (
            <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded mt-1 inline-block">
              {story.frequency.pattern}
            </span>
          )}
        </div>

        {/* Event */}
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Event</div>
          <div className="text-gray-700">
            {story.event.trigger && (
              <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs mr-1">
                {story.event.trigger}
              </span>
            )}
            {story.event.object && (
              <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-xs">
                {story.event.object}
              </span>
            )}
            {!story.event.trigger && !story.event.object && (
              <span className="text-gray-400">None specified</span>
            )}
          </div>
          {/* Companions — the "who were you with" answer. Shown here so an
              operator auditing a companion bonus can see both sides' sets
              side by side. */}
          {((story.event.companions?.you?.length ?? 0) > 0 ||
            (story.event.companions?.them?.length ?? 0) > 0) && (
            <div className="mt-1 space-y-1">
              {(story.event.companions?.you?.length ?? 0) > 0 && (
                <div className="flex items-start gap-1">
                  <span className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5">
                    With you
                  </span>
                  <ChipList items={story.event.companions!.you} color="green" />
                </div>
              )}
              {(story.event.companions?.them?.length ?? 0) > 0 && (
                <div className="flex items-start gap-1">
                  <span className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5">
                    With them
                  </span>
                  <ChipList items={story.event.companions!.them} color="blue" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* You Profile */}
        <ProfileSummary profile={story.youProfile} label="You (narrator)" />

        {/* Them Profile */}
        <ProfileSummary profile={story.themProfile} label="Them (person)" />

        {/* Emotional Tags */}
        {story.emotionalTags?.length > 0 && (
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Emotions</div>
            <ChipList items={story.emotionalTags} color="purple" />
          </div>
        )}
      </div>
    </div>
  );
}

export default function SideBySide({
  storyA,
  storyB,
  labelA = 'Story A (Query)',
  labelB = 'Story B (Match)',
  subtitleA,
  subtitleB,
  title = 'Story Comparison',
  showMirror = true,
}: Props) {
  return (
    <div className="card" data-testid="side-by-side">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">{title}</h2>

      <div className="grid md:grid-cols-2 gap-4">
        <StoryCard story={storyA} label={labelA} subtitle={subtitleA} bgClass="bg-blue-50" />
        <StoryCard story={storyB} label={labelB} subtitle={subtitleB} bgClass="bg-green-50" />
      </div>

      {/* Mirror arrows. Meaningless when the two cards are the same person on
          two paths, so PD-5's legacy-vs-split comparison turns them off. */}
      {showMirror && (
        <div className="mt-4 pt-4 border-t text-center text-sm text-gray-500">
          <div className="flex items-center justify-center gap-2">
            <span>A.them</span>
            <span className="text-xl">↔</span>
            <span>B.you</span>
            <span className="mx-4">|</span>
            <span>B.them</span>
            <span className="text-xl">↔</span>
            <span>A.you</span>
          </div>
          <div className="text-xs text-gray-400 mt-1">
            Mirror consistency: descriptions should align between perspectives
          </div>
        </div>
      )}
    </div>
  );
}
